// BridgeSelectorRow — Multi-Bridge UI.
//
// A horizontally-scrollable row of weighbridge cards plus a synced dropdown,
// shared verbatim between the main WB ticket workspace and the kiosk flow
// (identical logic; only the palette differs via the `variant` prop). Each
// card shows the bridge's live weight (filtered from the SSE weight_update
// feed by weighbridge_id), a live camera thumbnail (camera_frame SSE, ~2s,
// only refreshed while that bridge is the active/watched one), a connection
// status dot, and an "Active" badge when selected.
//
// The row owns the SINGLE live-feed subscription for its surface: it filters
// per bridge into local maps for display, and reports the ACTIVE bridge's
// reading up via onActiveReading so the parent can drive capture from exactly
// one source (no duplicate EventSource, no cross-bridge weight bleed).

import { useEffect, useRef, useState } from 'react';

import { useWbLiveFeed } from '../../hooks/wb/useWbLiveFeed';
import type { LiveBridge } from '../../hooks/wb/useWbLiveBridges';
import styles from './BridgeSelectorRow.module.css';

const KG = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });

interface BridgeReading {
  value: number;
  stable: boolean;
}

export interface BridgeSelectorRowProps {
  bridges: LiveBridge[];
  activeBridgeId: number | null;
  onSelect: (weighbridgeId: number) => void;
  /** Fires when the ACTIVE bridge's live weight updates — the parent feeds
   *  this into the shared capture readout. */
  onActiveReading?: (value: number, stable: boolean) => void;
  /** 'main' = WB workspace palette; 'kiosk' = --kiosk-* token palette;
   *  'sidebar' = kiosk palette in a vertical stacked layout. */
  variant?: 'main' | 'kiosk' | 'sidebar';
  /** Disable the SSE subscription (e.g. ticket locked / not on the live step). */
  feedEnabled?: boolean;
}

export function BridgeSelectorRow({
  bridges,
  activeBridgeId,
  onSelect,
  onActiveReading,
  variant = 'main',
  feedEnabled = true,
}: BridgeSelectorRowProps): JSX.Element {
  const [weightByBridge, setWeightByBridge] = useState<Record<number, BridgeReading>>({});
  const [frameByBridge, setFrameByBridge] = useState<Record<number, string>>({});

  // Keep the active id in a ref so the (ref-stable) feed callbacks always
  // forward the CURRENT active bridge's reading without re-subscribing.
  const activeIdRef = useRef(activeBridgeId);
  activeIdRef.current = activeBridgeId;
  const onActiveReadingRef = useRef(onActiveReading);
  onActiveReadingRef.current = onActiveReading;

  useWbLiveFeed({
    enabled: feedEnabled,
    onWeightUpdate: (ev) => {
      if (ev.weighbridgeId === null) return;
      setWeightByBridge((prev) => ({
        ...prev,
        [ev.weighbridgeId as number]: { value: ev.value, stable: ev.stable },
      }));
      if (ev.weighbridgeId === activeIdRef.current) {
        onActiveReadingRef.current?.(ev.value, ev.stable);
      }
    },
    onCameraFrame: (ev) => {
      if (ev.weighbridgeId === null) return;
      setFrameByBridge((prev) => ({ ...prev, [ev.weighbridgeId as number]: ev.frame }));
    },
  });

  // Drop cached weights/frames for bridges that have left the list.
  useEffect(() => {
    const present = new Set(bridges.map((b) => b.weighbridge_id));
    setWeightByBridge((prev) => prune(prev, present));
    setFrameByBridge((prev) => prune(prev, present));
  }, [bridges]);

  const isSidebar = variant === 'sidebar';
  const isKiosk = variant === 'kiosk' || isSidebar;
  const rootClass = isKiosk ? `${styles.root} ${styles.rootKiosk}` : styles.root;
  const scrollerClass = isSidebar ? styles.scrollerSidebar : styles.scroller;
  const headerClass = isSidebar ? `${styles.header} ${styles.headerSidebar}` : styles.header;
  const selectClass = isSidebar ? styles.selectSidebar : styles.select;

  if (bridges.length === 0) {
    return (
      <div className={rootClass} data-testid="bridge-selector-row">
        <span className={styles.empty}>No weighbridges connected.</span>
      </div>
    );
  }

  return (
    <div className={rootClass} data-testid="bridge-selector-row">
      <div className={headerClass}>
        <span className={styles.heading}>Weighbridges</span>
        <select
          className={selectClass}
          aria-label="Active weighbridge"
          data-testid="bridge-selector-dropdown"
          value={activeBridgeId ?? ''}
          onChange={(e) => {
            const id = Number(e.target.value);
            if (Number.isInteger(id)) onSelect(id);
          }}
        >
          {bridges.map((b) => (
            <option key={b.weighbridge_id} value={b.weighbridge_id}>
              {b.name}
              {b.agent_online ? '' : ' (disconnected)'}
            </option>
          ))}
        </select>
      </div>

      <div className={scrollerClass}>
        {bridges.map((b) => {
          const reading = weightByBridge[b.weighbridge_id] ?? null;
          const frame = frameByBridge[b.weighbridge_id] ?? null;
          const isActive = b.weighbridge_id === activeBridgeId;
          const cardClass = [
            isSidebar ? styles.bridgeCardSidebar : styles.card,
            isActive ? styles.cardActive : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={b.weighbridge_id}
              type="button"
              className={cardClass}
              aria-pressed={isActive}
              data-testid={`bridge-card-${b.weighbridge_id}`}
              onClick={() => onSelect(b.weighbridge_id)}
            >
              <div className={styles.cardTop}>
                <span className={styles.name} title={b.name}>
                  {b.name}
                </span>
                {isActive ? (
                  <span className={styles.activeBadge} data-testid="bridge-active-badge">
                    Active
                  </span>
                ) : null}
              </div>

              <div className={isSidebar ? styles.thumbnailSidebar : styles.thumb}>
                {frame !== null ? (
                  <img
                    className={styles.thumbImg}
                    src={`data:image/jpeg;base64,${frame}`}
                    alt={`${b.name} camera`}
                    data-testid={`bridge-thumb-${b.weighbridge_id}`}
                  />
                ) : (
                  <span className={styles.thumbPlaceholder}>No camera</span>
                )}
              </div>

              <div>
                <span className={styles.weight} data-testid={`bridge-weight-${b.weighbridge_id}`}>
                  {reading === null ? '—' : KG.format(reading.value)}
                </span>
                <span className={styles.weightUnit}>kg</span>
              </div>

              <div className={styles.status}>
                <span
                  className={`${styles.dot} ${b.agent_online ? styles.dotOnline : styles.dotOffline}`}
                  aria-hidden
                />
                {b.agent_online ? (
                  <span>Connected</span>
                ) : (
                  <span className={styles.statusError} data-testid={`bridge-status-error-${b.weighbridge_id}`}>
                    Disconnected
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function prune(map: Record<number, unknown>, present: Set<number>): Record<number, never> {
  const next: Record<number, never> = {};
  for (const key of Object.keys(map)) {
    const id = Number(key);
    if (present.has(id)) next[id] = map[id] as never;
  }
  return next;
}
